import styled from "styled-components";
import {
  getAlignment,
  getFrameAttributes,
  getJustification,
  getSecondaryAxis,
  getStyledNodeAttributes,
  getStyledTextAttributes,
} from "./helpers";
import {
  ButtonType,
  CollectionViewType,
  InputType,
  LabelType,
  LinearLayoutType,
  ViewType,
} from "./types";
import { PrefixWith$, ButtonStyle } from "../types";

// Styled Component definitions
export type ViewComponentProps = Parameters<typeof ViewComponent>[0];
export const ViewComponent = styled.div<
  PrefixWith$<ViewType> & { isSeparator: boolean; type: string }
>`
  ${(props) => getFrameAttributes(props.$frame)}
  ${(props) => getStyledNodeAttributes(props.$styles, props.isSeparator)}
 
   export ${(props) =>
    props.type === `button` && `&:hover { cursor: pointer; }`}
 
   > * {
    width: 100%;
    height: 100%;
  }
`;

export const LinearLayoutComponent = styled.div.attrs<
  PrefixWith$<Omit<LinearLayoutType, "children">>
>((props) => ({
  "data-direction": props.$direction,
}))<PrefixWith$<Omit<LinearLayoutType, "children">>>`
  display: flex;
  flex-direction: ${(props) =>
    props.$direction === `horizontal` ? `row` : `column`};
  flex-wrap: nowrap;
  justify-content: ${(props) =>
    getJustification(
      props.$distribution,
      props.$direction || `vertical`,
      props.$alignment,
    )};
  align-items: ${(props) =>
    getAlignment(props.$alignment, getSecondaryAxis(props.$direction))};
  gap: ${(props) => props.$spacing || 0}px;

  [data-direction="horizontal"] > && {
    ${(props) =>
      props.$frame?.width?.resize === `fill` || props.$resize?.width === `fill`
        ? `flex: 1;`
        : ``}
    ${(props) =>
      props.$frame?.height?.resize === `fill` ||
      props.$resize?.height === `fill`
        ? `align-self: stretch;`
        : ``}
     ${(props) =>
      props.$frame?.width?.resize === `hug`
        ? `max-width: ${props.$frame?.width?.min}px;`
        : ``}
  }

  [data-direction="vertical"] > && {
    ${(props) => (props.$resize?.height === `fill` ? `flex: 1;` : ``)}
    ${(props) => (props.$resize?.width === `fill` ? `width: 100%;` : ``)}
  }
`;

export const CollectionViewComponent = styled.div<
  PrefixWith$<CollectionViewType>
>`
  display: grid;
  width: 100%;

  grid-row-gap: ${(props) => props.$spacing?.vertical}px;
  grid-column-gap: ${(props) => props.$spacing?.horizontal}px;
  grid-template-columns: ${(props) =>
    props.$direction === `vertical`
      ? `100%`
      : `repeat(auto-fit, minmax(${
          props.$sections.cell.width?.min || 0
        }px, 1fr))`};
`;

export type ButtonComponentProps = Parameters<typeof ButtonComponent>[0];
export const ButtonComponent = styled.button<
  PrefixWith$<ButtonType> & { sx: { buttonStyle: ButtonStyle } }
>`
  background: none;
  border: none;
  font-family: inherit;
  ${(props) => getStyledNodeAttributes(props.$styles)}
  ${(props) => getFrameAttributes(props.$frame)}

  ${({ sx }) => {
    if (sx?.buttonStyle === `rectangular`) {
      return `border-radius: 0px;`;
    }
    if (sx?.buttonStyle === `rounded`) {
      return `border-radius: 2px;`;
    }
    if (sx?.buttonStyle === `pill`) {
      return `border-radius: 100px;`;
    }
    return null;
  }}

  &:focus {
    outline: none;
    box-shadow: none;
  }

  &:hover {
    cursor: pointer;
  }

  &:disabled {
    background: #e0e0e0 !important;
    border: 1px solid #e0e0e0 !important;
  }

  & span {
    display: inline-block;
  }
`;

export type LabelComponentProps = Parameters<typeof LabelComponent>[0];
export const LabelComponent = styled.div<PrefixWith$<LabelType>>`
  text-align: ${(props) => props.$alignment};
  ${(props) => getStyledTextAttributes(props.$styles)}
`;

export type InputComponentProps = Parameters<typeof InputComponent>[0];
export const InputComponent = styled.input<PrefixWith$<InputType>>`
   background: none;
   border: none;
   width: 100%;
   display: block;
   caret-color: ${(props) => props.$cursorColor}
   ${(props) => getStyledTextAttributes(props.$valueStyles)}
   ::placeholder {
     ${(props) => getStyledTextAttributes(props.$placeholderStyles)}
   }
 `;
